<template>
  <div
    :id="'StickyScrollListSection-' + props.block.id"
    :class="
      bem({
        'alignment-': props.block.titleAlignment,
        background: props.block.contentLoopSectionFormatting.background,
        'has-theme': !!props.block.theme,
      })
    "
    :style="
      props.block.theme &&
      `--highlight: ${props.block.theme.buttonBackgroundColour.hex}; --text: ${props.block.theme.buttonTextColour.hex}`
    "
  >
    <div
      :class="bem('title-wrapper')"
      :style="`--aspect-ratio: ${props.block.titleBackground.aspectRatio}`"
    >
      <MediaItem
        v-if="props.block.titleBackground"
        :item="props.block.titleBackground"
        aspect-ratio="auto"
        :class="bem('title-background')"
      />
      <div :class="bem('title-content')">
        <template
          v-for="item in props.block.titleContent"
          :key="item.id"
        >
          <VisionHeading
            v-if="item.type === 'title_block'"
            :tag="item.tag"
            :size="item.textSize"
            :class="bem('content-title')"
          >
            {{ item.text }}
          </VisionHeading>
          <p
            v-if="item.type === 'description'"
            :class="bem('description')"
          >
            {{ item.description }}
          </p>
          <div
            v-if="item.type === 'button_group'"
            :class="bem('button-group')"
          >
            <VisionButton
              v-for="button in item.buttons"
              :key="button.id"
              size="small"
              :to="buildInternalLink(button.link)"
              :outline="button.style === 'outline'"
              :background-colour="button.colour || 'white'"
              @click="handleButtonClick(`Title: ${button.text}`)"
            >
              <template #content>
                {{ button.text }}
              </template>
            </VisionButton>
          </div>
        </template>
      </div>
    </div>
    <div :class="bem('content-wrapper')">
      <div
        ref="stickyScrollListIndicator"
        :class="bem('scroll-indicator')"
      />
      <div
        v-for="item in props.block.content"
        :key="item.id"
        :class="bem('content-item')"
      >
        <div
          v-if="item.type === 'media_item'"
          :class="bem('media-item-wrapper')"
        >
          <MediaItem
            :item="item"
            :aspect-ratio="item.aspectRatio"
            :class="bem('media-item')"
          />
        </div>
        <template v-else-if="item.type === 'sticky_scroll_list_item'">
          <template v-for="listItem in item.content" :key="listItem.id">
            <VisionHeading
            v-if="listItem.type === 'title_block'"
            :tag="listItem.tag"
            :size="listItem.textSize"
            :class="bem('item-title')"
          >
            {{ listItem.text }}
          </VisionHeading>
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="listItem.type === 'description'"
            :class="bem('description')"
            v-html="listItem.description"
          />
          <!-- eslint-enable vue/no-v-html -->
          <h6 v-if="listItem.type === 'subtitle'" :class="bem('subtitle')">
            {{ listItem.subtitle }}
          </h6>
          <div
            v-if="listItem.type === 'button_group'"
            :class="bem('button-group')"
          >
            <VisionButton
              v-for="button in listItem.buttons"
              :key="button.id"
              size="small"
              :to="buildInternalLink(button.link)"
              :outline="button.style === 'outline'"
              :background-colour="button.colour || 'white'"
              @click="handleButtonClick(`Content: ${button.text}`)"
            >
              <template #content>
                {{ button.text }}
              </template>
            </VisionButton>
          </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { gsap } = useGsap()
const { $track } = useNuxtApp() as any
const { buildInternalLink } = useUrlBuilder()
const bem = useBEM('StickyScrollListSection')

interface StickyScrollListItem {
  id: string
  type: string
  mediaType: string
  video: any
  image: any
  reel: any[]
  speed: number
  aspectRatio:
    | '4 / 5'
    | '5 / 4'
    | '16 / 9'
    | '9 / 16'
    | '1 / 1'
    | '2 / 3'
    | 'auto'
  content: {
    id: string
    type: string
    text: string
    textSize: string
    tag: string
    style: string
    subtitle: string
    description: string
    buttons: {
      id: string
      style: string
      text: string
      link: any
      colour: string
    }[]
  }[]
}

interface StickyScrollListSectionProps {
  block: {
    id: string
    trackingId: string
    content: StickyScrollListItem[]
    titleAlignment: 'left' | 'right'
    titleContent: any[]
    titleBackground: any
    theme: any
    contentLoopSectionFormatting: {
      background: string
    }
  }
}

const props = defineProps<StickyScrollListSectionProps>()

const stickyScrollListIndicator = ref<HTMLElement | null>(null)

async function handleButtonClick(elementId: string) {
  await $track.global.linkClicked({
    interactionElement: elementId,
    interactionSection: props.block.trackingId,
    interactionCategory: 'Sticky Scroll List',
    linkText: elementId
  }, 'Clicked Sticky Scroll List Button')
}

onMounted(() => {
  if (stickyScrollListIndicator.value) {
    gsap.to(stickyScrollListIndicator.value, {
      scrollTrigger: {
        trigger: stickyScrollListIndicator.value,
        start: 'top 25%',
        end: 'bottom top',
        scrub: 1,
      },
      '--progress': 1,
    })
  }

  const stickyScrollContentItems = gsap.utils.toArray(
    `#StickyScrollListSection-${props.block.id} .StickyScrollListSection__content-item`
  )
  stickyScrollContentItems.forEach((contentItem: any) => {
    gsap.to(contentItem, {
      scrollTrigger: {
        trigger: contentItem,
        start: 'top bottom',
        end: 'top 70%',
        scrub: 1,
      },
      '--item-progress': 1,
    })
  })
})
</script>

<style lang="postcss">
.StickyScrollListSection {
  @apply w-full grid gap-8 lg:gap-14 lg:grid-cols-2;
}

.StickyScrollListSection__title-wrapper {
  --media-item-border-radius: 0;
  @apply relative aspect-[var(--aspect-ratio)] rounded-r-lg overflow-clip
  p-[var(--outer-gutter)] pr-4 smMax:pb-8
  flex flex-col justify-end gap-3;
  width: calc(100% - var(--outer-gutter));

  @screen lg {
    @apply aspect-auto w-full pr-14 gap-5 sticky top-[-8px];
    height: calc(100vh + 16px);
  }

  @screen full {
    padding-left: calc((100vw - var(--large-screen-size)) / 2 + var(--outer-gutter));
  }
}


.StickyScrollListSection__title-background {
  @apply absolute inset-0;
}

.StickyScrollListSection__title-content {
  @apply relative z-[1]
  flex flex-col gap-3
  text-white;

  @screen lg {
    @apply sticky gap-5 bottom-[var(--outer-gutter)];
  }
}

.StickyScrollListSection__subtitle {
  @apply font-fragment-mono text-xxs lg:text-xs text-grey-500;
}

.StickyScrollListSection__item-title {
  --vision-heading-size: 24px;

  @screen lg {
    --vision-heading-size: 32px;
  }

  @screen xxl {
    --vision-heading-size: 40px
  }
}

.StickyScrollListSection__content-wrapper {
  @apply relative pr-[var(--outer-gutter)] py-0 lg:py-20
  grid gap-8 lg:gap-14;
  padding-left: calc(var(--outer-gutter) * 2 + 2px);

  @screen lg {
    padding-left: 56px;
  }

  @screen full {
    padding-right: calc((100vw - var(--large-screen-size)) / 2 + var(--outer-gutter));
  }
}

.StickyScrollListSection__content-item {
  @apply grid gap-4 lg:gap-6;
  
  .StickyScrollListSection__description  {
    @apply text-grey-800;
  }
}

.StickyScrollListSection__content-title {
  @screen smMax {
    --vision-heading-size: 32px;
  }

  @screen xl {
    --vision-heading-size: 64px;
  }
}

.StickyScrollListSection__button-group {
  @apply mt-2 flex gap-3 lg:gap-4;
}

.StickyScrollListSection__description {
  @apply text-sm font-bold lg:text-base;

  p {
    @apply mt-3 lg:mt-4;

    &:first-child {
      @apply mt-0
    }
  }
}

.StickyScrollListSection__scroll-indicator {
  @apply absolute left-[var(--outer-gutter)] lg:left-0 top-0 w-0.5 h-full
  bg-grey-400 rounded-full overflow-clip;

  &::before {
    @apply content-[''] absolute inset-0 bg-grey-800 rounded-full origin-top;
    transform: scaleY(var(--progress));
  }
}

.StickyScrollListSection__media-item {
  @apply relative;
  transform: translate3d(calc(-15% + calc(var(--item-progress) * 15%)), 0, 0)
    scale(calc(1.2 - (0.2 * var(--item-progress))));

  &::before {
    @apply content-[''] absolute inset-0 z-[2] bg-black;
    opacity: clamp(0, calc(1 - var(--item-progress)), 0.8);
  }
}

.StickyScrollListSection__media-item-wrapper {
  clip-path: inset(0 calc(15% - (var(--item-progress) * 15%)) 0 0 round 8px);
}

/* Modifiers
  --alignment
  --background-dark
  --has-theme
*/
.StickyScrollListSection--alignment-right {
  .StickyScrollListSection__title-wrapper {
    @apply rounded-r-none rounded-l-lg;

    @screen lg {
      @apply col-start-2 col-end-2
    }
  }

  .StickyScrollListSection__content-wrapper {
    @apply pl-[var(--outer-gutter)] row-start-1 col-start-1;
    padding-right: calc(var(--outer-gutter) * 2 + 2px);

    @screen lg {
      padding-right: 56px;
    }

    @screen full {
      padding-right: calc((100vw - var(--large-screen-size)) / 2 + var(--outer-gutter));
    }
  }
}

.StickyScrollListSection--background-dark {
  .StickyScrollListSection__scroll-indicator {
    @apply bg-grey-800;

    &::before {
      @apply bg-white;
    }
  }
  .StickyScrollListSection__content-item {
    @apply text-white;
    
    .StickyScrollListSection__description  {
      @apply text-grey;
    }
  }
}

.StickyScrollListSection--has-theme {
  .StickyScrollListSection__scroll-indicator::before {
    @apply bg-[var(--highlight)];
  }
}
</style>
